import { Box, IconButton, Typography, Chip, Button } from "@mui/material";
import CustomDataGrid from "src/@core/components/data-grid";
import { styled } from "@mui/material/styles";
import { Edit, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import moment from "moment";

function TableModules({
  rows,
  totalCount,
  setCurrentPage,
  currentPage,
  setPageSize,
  pageSize,
  loading,
  toggleEdit,
  toggleStatus,
  toggleDelete,
  canEdit = true,
  canDelete = true,
}) {
  const navigate = useNavigate();
  const statusColors = {
    Active: "#66bb6a",
    Inactive: "#FFB400",
  };
  const CustomChip = styled(Chip)(({ label }) => ({
    backgroundColor: statusColors[label] || "#666",
    color: "#fff",
    width: "100px",
    textTransform: "capitalize",
  }));

  const rowsWithSrNo = rows.map((row, index) => ({
    ...row,
    srNo: (currentPage - 1) * pageSize + index + 1,
  }));

  const handleRowClick = (params) => {
    // Navigate to lessons page for the selected module
    navigate(`/lessons/${params.row.id}`);
  };

  // Check if any actions are available
  const hasAnyActions = canEdit || canDelete;

  // Base columns
  const baseColumns = [
    { field: "srNo", headerName: "Sr No", minWidth: 100, sortable: false },
    {
      field: "title",
      headerName: "Title",
      minWidth: 250,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography noWrap variant="body2" textTransform="capitalize">
          {row.title}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 400,
      sortable: false,
      renderCell: ({ row }) => {
        // Handle description as array or string
        const description = Array.isArray(row.description) 
          ? row.description.join(", ") 
          : row.description;
        return (
          <Typography 
            noWrap 
            variant="body2" 
            sx={{ 
              maxWidth: 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </Typography>
        );
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => <CustomChip
        label={row.is_active ? "Active" : "Inactive"}
      />,
    },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {moment(row.created_at).format("MMM DD, YYYY hh:mm A")}
        </Typography>
      ),
    },
  ];

  // Actions column - only include if there are any actions available
  const actionsColumn = hasAnyActions ? {
    field: "Actions",
    headerName: "Actions",
    minWidth: 300,
    sortable: false,
    renderCell: ({ row }) => {
      const isDeleted = row.status === 2;
      const disabledStyle = { color: "#B0B0B0" };
      return (
        <Box display="flex" gap="10px">
          {canEdit && (
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                toggleEdit(e, row);
              }}
              disabled={isDeleted}
            >
              <Edit sx={isDeleted ? disabledStyle : {}} />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                toggleDelete(e, row);
              }}
              disabled={isDeleted}
            >
              <Delete sx={isDeleted ? disabledStyle : {}} />
            </IconButton>
          )}
          {canEdit && (
            <Button variant="contained" color="primary" onClick={(e) => {
              e.stopPropagation();
              navigate(`/lessons/${row.id}`);
            }}>
              Add/Edit Lessons
            </Button>
          )}
        </Box>
      );
    },
  } : null;

  // Combine columns
  const columns = actionsColumn 
    ? [...baseColumns, actionsColumn]
    : baseColumns;

  return (
    <CustomDataGrid
      getRowId={(row) => row.id}
      loading={loading}
      rowCount={totalCount}
      rows={rowsWithSrNo}
      handleCellClick={handleRowClick}
      columns={columns}
      currentPage={currentPage}
      pageSize={pageSize}
      setCurrentPage={setCurrentPage}
      setPageSize={setPageSize}
    />
  );
}

export default TableModules;
